/**
 * A numeric range filter for a toolbar: a select-looking button over a panel
 * with From and To fields, either one optional ("at least" / "at most").
 * The value is in the unit the fields show (dollars, seconds); the page turns
 * it into what its API takes. `format` writes a bound for the button.
 */

import { useId, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ChevronDown } from 'lucide-react';

import { usePopover } from './usePopover';

export interface NumberRange {
  min: number | null;
  max: number | null;
}

export const ANY_RANGE: NumberRange = { min: null, max: null };

function parse(text: string): number | null {
  const n = text.trim() === '' ? NaN : Number(text);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function rangeSummary(value: NumberRange, format: (n: number) => string, allLabel: string): string {
  if (value.min !== null && value.max !== null) return `${format(value.min)} – ${format(value.max)}`;
  if (value.min !== null) return `≥ ${format(value.min)}`;
  if (value.max !== null) return `≤ ${format(value.max)}`;
  return allLabel;
}

/** The typed bounds, and whether they make a range worth applying. */
function readDraft(from: string, to: string): { low: number | null; high: number | null; valid: boolean } {
  const low = parse(from);
  const high = parse(to);
  const typedBad = (from.trim() !== '' && low === null) || (to.trim() !== '' && high === null);
  const ordered = low === null || high === null || low <= high;
  return { low, high, valid: !typedBad && (low !== null || high !== null) && ordered };
}

function adornmentClass(prefix?: string, unit?: string): string | undefined {
  if (prefix) return 'input-prefix';
  return unit ? 'input-suffix' : undefined;
}

interface BoundInputProps {
  id: string;
  text: string;
  onText: (text: string) => void;
  placeholder: string;
  prefix?: string;
  unit?: string;
  step: string;
}

function BoundInput({ id, text, onText, placeholder, prefix, unit, step }: BoundInputProps) {
  return (
    <div className={adornmentClass(prefix, unit)}>
      {prefix && <span className="prefix" aria-hidden="true">{prefix}</span>}
      <input id={id} type="number" inputMode="decimal" min={0} step={step} value={text} placeholder={placeholder} onChange={(e) => onText(e.target.value)} />
      {!prefix && unit && <span className="suffix" aria-hidden="true">{unit}</span>}
    </div>
  );
}

function RangeForm({
  panelId,
  initial,
  active,
  adornment,
  onApply,
}: {
  panelId: string;
  initial: NumberRange;
  active: boolean;
  adornment: { prefix?: string; unit?: string; step: string };
  onApply: (next: NumberRange) => void;
}) {
  const [from, setFrom] = useState(initial.min === null ? '' : String(initial.min));
  const [to, setTo] = useState(initial.max === null ? '' : String(initial.max));
  const { low, high, valid } = readDraft(from, to);

  return (
    <form
      className="filter-popover-custom"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) onApply({ min: low, max: high });
      }}
    >
      <div className="field">
        <label className="label" htmlFor={`${panelId}-from`}>From</label>
        <BoundInput id={`${panelId}-from`} text={from} onText={setFrom} placeholder="No minimum" {...adornment} />
      </div>
      <div className="field">
        <label className="label" htmlFor={`${panelId}-to`}>To</label>
        <BoundInput id={`${panelId}-to`} text={to} onText={setTo} placeholder="No maximum" {...adornment} />
      </div>
      <p className="hint filter-popover-note">Both ends included; leave one empty for an open range.</p>
      <div className="filter-popover-actions">
        <button type="button" className="btn btn-ghost btn-sm" disabled={!active} onClick={() => onApply(ANY_RANGE)}>
          Clear
        </button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={!valid}>
          Apply
        </button>
      </div>
    </form>
  );
}

export function RangeFilter({
  label,
  allLabel,
  Icon,
  unit,
  prefix,
  step = 'any',
  value,
  format,
  onChange,
}: {
  /** Names the filter for screen readers and the panel, e.g. "Cost". */
  label: string;
  /** What the button says while no bound is set, e.g. "Any cost". */
  allLabel: string;
  Icon: LucideIcon;
  /** Shown after the fields, e.g. "s". */
  unit?: string;
  /** Shown before the fields, e.g. "$". */
  prefix?: string;
  step?: string;
  value: NumberRange;
  format: (n: number) => string;
  onChange: (value: NumberRange) => void;
}) {
  const popover = usePopover();
  const panelId = useId();
  const active = value.min !== null || value.max !== null;
  const summary = rangeSummary(value, format, allLabel);

  function apply(next: NumberRange) {
    onChange(next);
    popover.close();
  }

  return (
    <>
      <button
        ref={popover.button}
        type="button"
        className={`multi-select range-filter has-lead${active ? ' has-value' : ''}`}
        aria-label={`${label}: ${summary}`}
        title={summary}
        aria-haspopup="dialog"
        aria-expanded={popover.open}
        aria-controls={popover.open ? panelId : undefined}
        onClick={popover.toggle}
      >
        <Icon className="select-lead-icon" aria-hidden="true" />
        <span className="multi-select-value">{summary}</span>
        <ChevronDown aria-hidden="true" />
      </button>
      {popover.open && (
        <div ref={popover.panel} id={panelId} className="user-dropdown filter-popover" role="dialog" aria-label={label} style={popover.style}>
          {/* Mounted on each open, so the fields start from the applied range. */}
          <RangeForm panelId={panelId} initial={value} active={active} adornment={{ prefix, unit, step }} onApply={apply} />
        </div>
      )}
    </>
  );
}
